import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  input,
  viewChild
} from '@angular/core';
import { gsap } from 'gsap';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-rtis-logo',
  templateUrl: './rtis-logo.component.html',
  styleUrls: ['./rtis-logo.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'rtis-logo',
    '[class.rtis-logo-dark]': "theme() === 'dark'",
    '[class.rtis-logo-light]': "theme() === 'light'",
    '[style.width]': 'width()'
  }
})
export class RtisLogoComponent implements AfterViewInit, OnDestroy {
  readonly logo = viewChild.required<ElementRef<HTMLElement>>('logo');
  readonly animated = input(false);
  readonly theme = input<'light' | 'dark'>('light');
  readonly width = input('10em');
  private ctx?: gsap.Context;
  ngAfterViewInit() {
    // Static logo unless explicitly animated; the SVG already renders its
    // finished state (bars full, text + arrow filled via currentColor).
    if (!this.animated()) {
      return;
    }
    // Respect reduced-motion: the SVG already renders its finished state, so
    // just leave it static.
    if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const el = this.logo().nativeElement;
    // Bars ordered left-to-right (red, yellow, blue, green) — the direction the
    // chart ascends — so the rise reads as growth rather than right-to-left.
    const bars = ['.cls-1', '.cls-5', '.cls-4', '.cls-3']
      .map(sel => el.querySelector(sel))
      .filter((n): n is Element => !!n);
    const arrow = el.querySelector('#growth-arrow') as SVGGeometryElement | null;
    const iriText = el.querySelector('#iri-text');
    const rtisText = el.querySelectorAll('#rtis-text path');
    if (bars.length !== 4 || !arrow || !iriText || !rtisText.length) {
      return;
    }
    // Resolve the active color from the theme so the arrow/text track
    // light vs dark instead of being hardcoded white.
    const strokeColor = getComputedStyle(el).color || '#fff';
    const length = arrow.getTotalLength();
    // Scope every tween to this element so ngOnDestroy().revert() cleans up
    // the repeating timeline instead of leaking it onto detached nodes.
    this.ctx = gsap.context(() => {
      // Pre-state for the very first frame (the timeline re-applies these at
      // position 0 on every repeat so each loop rebuilds from scratch).
      gsap.set(bars, { transformOrigin: 'bottom center', scaleY: 0 });
      gsap.set(iriText, { transformOrigin: '38px 36px' });

      // One orchestrated cycle, then a readable pause before it replays: the
      // chart fills, the trend line plots uphill and crests with the tallest
      // bar, then the wordmark resolves.
      gsap
        .timeline({ repeat: -1, repeatDelay: 2.8, defaults: { ease: 'power3.out' } })
        // reset to pre-state at the start of each iteration
        .set(bars, { scaleY: 0 }, 0)
        .set(
          arrow,
          {
            opacity: 0,
            fill: 'none',
            stroke: strokeColor,
            strokeWidth: 2,
            strokeDasharray: length,
            // negative offset so the line draws uphill, tail-to-head
            strokeDashoffset: -length
          },
          0
        )
        .set(rtisText, { opacity: 0, yPercent: 40 }, 0)
        .set(iriText, { opacity: 0, scale: 0.6 }, 0)
        // bars rise in a left-to-right wave with a small overshoot/settle
        .to(bars, { scaleY: 1, duration: 0.5, stagger: 0.09, ease: 'back.out(2.2)' }, 0)
        // trend line plots uphill, timed to crest with the bars
        .set(arrow, { opacity: 1 }, 0.12)
        .to(arrow, { strokeDashoffset: 0, duration: 0.95, ease: 'power2.inOut' }, 0.12)
        // arrowhead fills solid once the line finishes
        .to(
          arrow,
          { fill: strokeColor, strokeWidth: 0, duration: 0.3, ease: 'power1.out' },
          '>-0.05'
        )
        // IRI mark pops in
        .to(iriText, { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(2)' }, 0.5)
        // RTIS letters rise into place
        .to(rtisText, { opacity: 1, yPercent: 0, duration: 0.5, stagger: 0.07 }, 0.35)
        // ease the whole cycle (and the hold) down to a calmer pace
        .timeScale(0.8);
    }, el);
  }
  ngOnDestroy(): void {
    this.ctx?.revert();
  }
}
